 
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import {
    NSTable,
    NSCellInput,
} from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { formatISOStartOfMonth } from 'ns_libs/formatter';
import {
    OPERATIONS_INCOME_WS_HANDLERS, CASH_FLOW_TOOLTIP_MESSAGE, tableValueClassName,
} from 'views/ProFormaTable/constants';
import { IFormattedCashFlowPeriod, ITenantRecovery } from 'views/ProFormaTable/types';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import PeriodsSelect from '../../../../PeriodsSelect/PeriodsSelect';
import './TenantsRecoveryTable.scss';

export interface ITenantsRecoveryTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
    monthlyPeriodOptions: IFormattedCashFlowPeriod[];
}

const TenantsRecoveryTable = ({
    operationBeginIndex, baseCashFlowColumns, monthlyPeriodOptions,
}: ITenantsRecoveryTableProps) => {
    const {
        incomeSummary, handleUpdateOperationsRow,
        showCents, formatDisplayValue, operationsCashFlow,
    } = useProFormaSocketContext();

    const handleUpdateTenant = (id: number, field: string) => handleUpdateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.TENANT.UPDATE, id, field);

    const recoveryCashFlowColumns: ColumnDef<ITenantRecovery>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `tenant-recovery-period-${column.id}`,
        accessorFn: (row, tenantIndex: number) => {
            const hasCashFlow = row.startMonth
                && typeof operationsCashFlow?.[row.startMonth]?.tenantRecoveries?.[tenantIndex]?.value === 'number';
            return (
                hasCashFlow
                    ? (operationsCashFlow?.[cashFlowIndex]?.tenantRecoveries?.[tenantIndex]?.value || 0)
                    : null
            );
        },
        footer: () => (
            <span className="text-dark">
                {operationBeginIndex
                    ? formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.totalRecoveryIncome || 0, showCents, 'currency')
                    : ' '}
            </span>
        ),
    }));

    const recoveryColumns: ColumnDef<ITenantRecovery>[] = [
        {
            accessorKey: 'tenant',
            id: 'tenant',
            header: () => <span className="pl-1">Recovery</span>,
            cell: ({ row, getValue }) => <span className="pl-1 text-dark">{getValue() as string}</span>,
            footer: () => <span className="pl-1 text-nowrap">Total recovery income</span>,
        },
        {
            accessorKey: 'recoveryStart',
            id: 'recoveryStart',
            header: () => <span>Rec. start</span>,
            cell: ({ row, getValue }) => (
                <PeriodsSelect
                    compact
                    hasInvertedColors
                    handleChange={value => {
                        const updateFn = handleUpdateTenant(Number(row.original.id), 'recoveryStart');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    options={monthlyPeriodOptions}
                    value={!getValue() ? null
                        : formatISOStartOfMonth(getValue() as string)}
                />
            ),
        },
        {
            accessorKey: 'recoveryPercentage',
            id: 'recoveryPercentage',
            header: () => <span>Rec. %</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    numberType="percentage"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateTenant(Number(row.original.id), 'recoveryPercentage');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter percentage"
                />
            ),
        },
        {
            accessorKey: 'proRataShare',
            id: 'proRataShare',
            header: () => <span>Pro rata share</span>,
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue())}>
                    {formatDisplayValue(getValue(), true, 'percentage')}
                </span>
            ),
        },
        {
            accessorKey: 'recoveryValuePerSfPerYear',
            id: 'recoveryValuePerSfPerYear',
            header: () => <span>$/RSF/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateTenant(Number(row.original.id), 'recoveryValuePerSfPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.totalRecoveryIncome?.totalValuePerSfPerYear || 0, true, 'currency')}</span>,
        },
        {
            accessorKey: 'recoveryAmountPerYear',
            id: 'recoveryAmountPerYear',
            header: () => <span className="pr-1">Amount/year</span>,
            cell: ({ row, getValue }) => (
                <div className="pr-1">
                    <NSCellInput
                        showCents={showCents}
                        type="number"
                        numberType="currency"
                        value={getValue()}
                        handleUpdateValue={updatedValue => {
                            const updateFn = handleUpdateTenant(Number(row.original.id), 'recoveryAmountPerYear');
                            if (typeof updateFn === 'function') {
                                updateFn({
                                    target: { value: updatedValue },
                                } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        isRightAligned
                        placeholder="Enter amount"
                    />
                </div>
            ),
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(incomeSummary.totalRecoveryIncome?.totalAmountPerYear || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...recoveryCashFlowColumns,
    ];

    const table = useReactTable({
        data: incomeSummary.tenantRecoveries || [],
        columns: recoveryColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className="NSTable TenantsRecoveryTable mb-1">
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                })}
            >
                <RenderIf isTrue={table.getRowModel().rows.length}>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </RenderIf>
                <RenderIf isTrue={!table.getRowModel().rows.length}>
                    <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        <td colSpan={6} className="NSTable__tbody__tr__td text-center text-muted py-4">
                            <div className="text-dark">Your added tenants will appear here.</div>
                            <div className="small">
                                Click the
                                {' '}
                                <span className="font-weight-bold text-dark">[+]</span>
                                {' '}
                                symbol in the
                                {' '}
                                <span className="font-weight-bold text-dark">Tenant</span>
                                {' '}
                                header to add your first tenant.
                            </div>
                        </td>
                        {baseCashFlowColumns.map((column, index) => (
                            <td
                                key={`tenant-recovery-period-${column.id}`}
                                className="NSTable__tbody__tr__td"
                                id={`tenant-placeholder-period-${index}`}
                            >
                                <RenderIf isTrue={baseCashFlowColumns.length === 1 && index === 0}>
                                    <UncontrolledTooltip target={`tenant-placeholder-period-${index}`}>
                                        {CASH_FLOW_TOOLTIP_MESSAGE}
                                    </UncontrolledTooltip>
                                </RenderIf>
                            </td>
                        ))}
                    </tr>
                </RenderIf>
            </tbody>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default TenantsRecoveryTable;
